import { ArrowRightIcon } from '@f8n/icons';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { P, match } from 'ts-pattern';

import NftMedia from 'components/NftMedia';
import TransactionModal from 'components/TransactionModal';
import TransactionModalContent from 'components/TransactionModalContent';
import Box from 'components/base/Box';
import Button from 'components/base/Button';
import Modal from 'components/base/Modal';
import Text from 'components/base/Text';
import { MintWidgetContext } from 'components/mint-widget/MintWidgetContext';
import { MintWidgetMintCta } from 'components/mint-widget/MintWidgetMintCta';
import { MintWidgetQuantityStepper } from 'components/mint-widget/MintWidgetQuantityStepper';
import SuccessModal from 'components/modals/SuccessModal';
import { MintFromFixedPriceContext } from 'flows/mint-from-fixed-price/MintFromFixedPriceContext';
import { MintFromFoundationFixedPriceProvider } from 'flows/mint-from-fixed-price/MintFromFoundationFixedPriceProvider';
import { MintFromHighlightFixedPriceProvider } from 'flows/mint-from-fixed-price/MintFromHighlightFixedPriceProvider';
import useTransactionStore from 'state/stores/transactions';

import { useDrop } from 'gql/api/queries/drop.generated';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useWeb3ActionCta } from 'hooks/web3/use-web3-action-cta';
import { getAddress } from 'utils/address';
import { formatBigIntEthWithSuffix } from 'utils/formatters';
import { isNumberType, noop } from 'utils/helpers';
import { getCollectionFilter } from 'utils/inputs';
import { getChainConfigById } from 'utils/network';
import { getPath } from 'utils/router';
import { selectTransactionByAction } from 'utils/transaction-tracking';
import { buildMintTweet } from 'utils/twitter-templates';

import { Marketplace } from 'types/Marketplace';
import { MintPriceBreakdown } from 'types/MintFee';
import { TxHash } from 'types/Transactions';
import { ExperimentalWeb3ActionConfig } from 'types/Web3';
import { ModalOfType } from 'types/modal';
import { StepperConfig } from 'types/stepper';

type MintModalAction = 'mint-from-drop' | 'mint-from-edition';

const MODAL_KEY = 'MINT';

type MintModalOptions = ModalOfType<typeof MODAL_KEY>;

export default function MintModal() {
  const modal = useModalVisibility('MINT');

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && (
            <MintModalFlowManager
              action={
                // TODO: update this to work with dutch auctions + add payload for highlight
                modal.config.category === 'DROP'
                  ? 'mint-from-drop'
                  : 'mint-from-edition'
              }
              config={modal.config}
            />
          )}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function MintModalFlowManager(props: {
  action: MintModalAction;
  config: MintModalOptions;
}) {
  const { action, config } = props;

  const [mintQuantity, setMintQuantity] = useState(1);

  // TODO: find a simpler way to conditionally fetch the sale status for FND Drops
  // We need to do this conditionally because it relates to presales inside MintFromFoundationFixedPriceProvider
  const dropQuery = useDrop(getCollectionFilter(config.collection), {
    enabled: config.category === 'DROP' && config.market === 'FND',
    select: (data) => {
      return match(data)
        .with(
          {
            drop: {
              saleConfiguration: {
                __typename: 'FixedPriceSaleConfiguration',
                fndFixedPriceSaleStatus: P.select('saleStatus'),
              },
            },
          },
          ({ saleStatus }) => {
            return { saleStatus };
          }
        )
        .otherwise(() => null);
    },
  });

  return match(config)
    .with({ category: 'DROP', market: 'FND' }, (fndConfig) => {
      const dropSaleStatus =
        dropQuery.isSuccess && dropQuery.data
          ? dropQuery.data.saleStatus
          : null;

      if (!dropSaleStatus) return null;

      return (
        <MintFromFoundationFixedPriceProvider
          chainId={fndConfig.collection.chainId}
          contractAddress={fndConfig.collection.contractAddress}
          contractCategory={fndConfig.category}
          mintQuantity={mintQuantity}
          price={fndConfig.mintPrice}
          setMintQuantity={setMintQuantity}
          saleStatus={dropSaleStatus}
        >
          <MintFromFixedPriceContext.Consumer>
            {(context) => {
              if (!context) return null;

              return (
                <MintModalWindowConnected
                  action={action}
                  actionConfig={context.config}
                  market={fndConfig.market}
                  modalConfig={config}
                  pricing={context.pricing}
                  quantity={context.quantity}
                />
              );
            }}
          </MintFromFixedPriceContext.Consumer>
        </MintFromFoundationFixedPriceProvider>
      );
    })
    .with({ market: 'FND' }, (fndConfig) => {
      return (
        <MintFromFoundationFixedPriceProvider
          chainId={fndConfig.collection.chainId}
          contractAddress={fndConfig.collection.contractAddress}
          contractCategory={fndConfig.category}
          mintQuantity={mintQuantity}
          price={fndConfig.mintPrice}
          setMintQuantity={setMintQuantity}
        >
          <MintFromFixedPriceContext.Consumer>
            {(context) => {
              if (!context) return null;

              return (
                <MintModalWindowConnected
                  action={action}
                  actionConfig={context.config}
                  market={fndConfig.market}
                  modalConfig={config}
                  pricing={context.pricing}
                  quantity={context.quantity}
                />
              );
            }}
          </MintFromFixedPriceContext.Consumer>
        </MintFromFoundationFixedPriceProvider>
      );
    })
    .with({ market: 'HIGHLIGHT' }, (highlightConfig) => {
      return (
        <MintFromHighlightFixedPriceProvider
          chainId={highlightConfig.collection.chainId}
          mintQuantity={mintQuantity}
          sale={highlightConfig.sale}
          contractAddress={getAddress(
            highlightConfig.collection.contractAddress
          )}
          setMintQuantity={setMintQuantity}
        >
          <MintFromFixedPriceContext.Consumer>
            {(context) => {
              if (!context) return null;

              return (
                <MintModalWindowConnected
                  action={action}
                  actionConfig={context.config}
                  market={highlightConfig.market}
                  modalConfig={config}
                  pricing={context.pricing}
                  quantity={context.quantity}
                />
              );
            }}
          </MintFromFixedPriceContext.Consumer>
        </MintFromHighlightFixedPriceProvider>
      );
    })
    .exhaustive();
}

function MintModalWindowConnected(props: {
  action: MintModalAction;
  actionConfig: ExperimentalWeb3ActionConfig;
  market: Marketplace;
  modalConfig: MintModalOptions;
  pricing: MintPriceBreakdown;
  quantity: StepperConfig;
}) {
  const { action, actionConfig, market, modalConfig, pricing, quantity } =
    props;

  const trackedTx = useTransactionStore(
    selectTransactionByAction({
      action,
      txHash: actionConfig.txHash,
    })
  );

  const mintCta = useWeb3ActionCta({
    chainId: modalConfig.collection.chainId,
    config: actionConfig,
    context: 'tx-modal',
    trackedTx,
  });

  return (
    <MintWidgetContext.Provider
      value={{
        // These noop features are never shown inside the MintModal
        onAddToCal: noop,
        onCollectorsClick: noop,
        onShopTheSecondary: noop,

        market,
        pricing,
        quantity,
        mint: mintCta,
      }}
    >
      <MintModalWindow
        action={action}
        config={modalConfig}
        txHash={trackedTx ? trackedTx.txHash : null}
      />
    </MintWidgetContext.Provider>
  );
}

function MintModalWindow(props: {
  action: MintModalAction;
  config: MintModalOptions;
  txHash: TxHash | null;
}) {
  const { action, config, txHash } = props;
  const { chainId } = config.collection;

  if (!isNumberType(config.mintPrice)) {
    return null;
  }

  const chainConfig = getChainConfigById(chainId);

  return (
    <TransactionModal
      chainId={chainId}
      enforceBaseBalance
      preConnectPrompt={{
        description: match(chainConfig)
          .with({ chain: 'base' }, { chain: 'baseSepolia' }, (baseConfig) => {
            return baseConfig.copy.chainBenefits;
          })
          .otherwise(() => {
            return `Switch to ${chainConfig.copy.verboseChainName} to mint from ${props.config.collection.name}.`;
          }),
        title: `This artwork is on the ${chainConfig.copy.chainName} network`,
      }}
      preSignPrompt={{ type: 'skip' }}
      preSwitchNetworkPrompt={{
        description: match(chainConfig)
          .with({ chain: 'base' }, { chain: 'baseSepolia' }, (baseConfig) => {
            return baseConfig.copy.chainBenefits;
          })
          .otherwise(() => {
            return `Switch to ${chainConfig.copy.verboseChainName} to mint from ${props.config.collection.name}.`;
          }),
        title: `This artwork is on the ${chainConfig.copy.chainName} network`,
      }}
    >
      <MintModalCheckout action={action} config={config} txHash={txHash} />
    </TransactionModal>
  );
}

type MintModalCheckoutProps = {
  action: MintModalAction;
  config: MintModalOptions;
  txHash: TxHash | null;
};

function MintModalCheckout(props: MintModalCheckoutProps) {
  const { action, config, txHash } = props;
  const { collection, media } = config;

  const router = useRouter();

  const collectionPath = getPath.mint.page(collection);

  const twitterShareText = buildMintTweet({
    collectionName: collection.name,
    collectionPath: getPath.mint.page(collection),
  });

  return (
    <TransactionModalContent
      action={action}
      chainId={collection.chainId}
      txHash={txHash}
      header={<Modal.Header title="Mint" />}
      footer={
        <Modal.Footer>
          <MintWidgetMintCta size={1} />
        </Modal.Footer>
      }
      size={1}
      renderOnSuccess={(action) => {
        const isSingularMint = action.quantity === 1;

        const mintPriceSummary = (
          <Text weight="semibold" as="span" color="strong">
            {formatBigIntEthWithSuffix(action.txPrice)}
          </Text>
        );

        return {
          body: (
            <SuccessModal.Body
              media={media}
              description={
                isSingularMint ? (
                  <>
                    Your NFT was minted for a price of {mintPriceSummary}. It's
                    now in your wallet and displayed on your profile.
                  </>
                ) : (
                  <>
                    Your NFTs were minted for a price of {mintPriceSummary}.
                    They are now in your wallet and displayed on your profile.
                  </>
                )
              }
              title={isSingularMint ? 'You got it!' : 'You got them!'}
            />
          ),
          footer: (
            <SuccessModal.Footer
              shareText={twitterShareText}
              extraCta={
                <Button
                  icon
                  as={Modal.Close}
                  onClick={() => router.push(collectionPath)}
                >
                  <span>Collection</span>
                  <ArrowRightIcon />
                </Button>
              }
            />
          ),
        };
      }}
    >
      <Box>
        {media && <NftMedia media={media} />}
        <MintWidgetQuantityStepper />
      </Box>
    </TransactionModalContent>
  );
}
